import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Phone, User } from "lucide-react";
import { API, PHONE_TEL, PHONE_DISPLAY } from "@/lib/site";

const SERVICES = ["Free Demo", "Online Marketing", "SEO", "Website Development", "AI Chatbots", "Booking Systems", "Hosting Systems", "Full Package"];
const BUDGETS = ["< $2k", "$2k – $5k", "$5k – $15k", "$15k+", "Not sure yet"];

const initial = { name: "", email: "", company: "", service_interest: "", budget_range: "", message: "" };

const inputCls =
  "w-full px-4 py-3.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition-shadow";

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_interest) return toast.error("Please select a service interest.");
    if (!form.budget_range) return toast.error("Please select a budget range.");
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Enquiry sent — we'll get back to you within 2 business hours.");
      setForm(initial);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-slate-100/70 dark:bg-slate-900/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-4">Get started</p>
          <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Start Your Growth Engine</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-md">
            Tell us about your project. Our engineering team responds within 2 business hours.
          </p>
          <ul className="mt-10 space-y-5">
            {[
              ["01", "We review your requirements and current stack."],
              ["02", "You get a 15-minute strategy call and a scoped proposal."],
              ["03", "We build, launch, host, and grow it — all under one roof."],
            ].map(([n, t]) => (
              <li key={n} className="flex items-start gap-4">
                <span className="font-display font-black text-primary/30 text-2xl leading-none">{n}</span>
                <span className="text-sm sm:text-base text-slate-600 dark:text-slate-300 pt-0.5">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="contact-leadership">
            {[
              ["Lual Achour", "Managing Director"],
              ["Wilson Lual", "Operations Director"],
            ].map(([name, role]) => (
              <div key={name} className="flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <span className="p-2.5 rounded-full bg-primary/10 text-primary shrink-0"><User className="w-4 h-4" /></span>
                <div>
                  <p className="font-semibold text-sm">{name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{role}</p>
                </div>
              </div>
            ))}
          </div>
          <a href={PHONE_TEL} data-testid="contact-phone-link"
             className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <Phone className="w-4 h-4" /> Prefer to talk? Call us on {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.form onSubmit={submit} data-testid="contact-form"
                     initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                     viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}
                     className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-10 shadow-xl shadow-slate-900/5">
          <div className="grid sm:grid-cols-2 gap-5">
            <input required minLength={2} placeholder="Your name *" value={form.name}
                   onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="contact-form-name" />
            <input required type="email" placeholder="Email address *" value={form.email}
                   onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="contact-form-email" />
          </div>
          <input placeholder="Company name" value={form.company}
                 onChange={(e) => set("company", e.target.value)} className={`${inputCls} mt-5`} data-testid="contact-form-company" />
          <select required value={form.service_interest} onChange={(e) => set("service_interest", e.target.value)}
                  className={`${inputCls} mt-5 ${!form.service_interest ? "text-slate-400" : ""}`} data-testid="contact-form-service-select">
            <option value="" disabled>Service interest *</option>
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="mt-5" data-testid="contact-form-budget-select">
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3">Budget range *</p>
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map((b) => (
                <button type="button" key={b} onClick={() => set("budget_range", b)} data-testid={`budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                          form.budget_range === b
                            ? "bg-primary text-white border-primary"
                            : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-primary hover:text-primary"
                        }`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
          <textarea required minLength={5} rows={4} placeholder="Tell us about your project *" value={form.message}
                    onChange={(e) => set("message", e.target.value)} className={`${inputCls} mt-5 resize-none`} data-testid="contact-form-message" />
          <button type="submit" disabled={sending} data-testid="contact-form-submit-button"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-white font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
